import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface InfoTooltipProps {
  children: ReactNode;
  /** Accessible name for the trigger. */
  label?: string;
  className?: string;
}

const GAP = 8;
const ESTIMATED_HEIGHT = 96;

/**
 * An info icon whose bubble is rendered on top of the page rather than beside
 * the icon.
 *
 * Tables scroll horizontally and are wrapped in `overflow-hidden` cards, so a
 * bubble positioned inside them gets cut off at the edges — badly on the last
 * row. Painting it into the body through a portal, at coordinates measured from
 * the icon, keeps it whole wherever the icon happens to be.
 */
export default function InfoTooltip({
  children,
  label = "More information",
  className = "",
}: InfoTooltipProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, above: false });

  const place = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Flip above the icon when there is not enough room underneath.
    const above = rect.bottom + ESTIMATED_HEIGHT + GAP > window.innerHeight;

    setPosition({
      top: above ? rect.top - GAP : rect.bottom + GAP,
      left: rect.left + rect.width / 2,
      above,
    });
  }, []);

  const show = useCallback(() => {
    place();
    setOpen(true);
  }, [place]);

  const hide = useCallback(() => setOpen(false), []);

  // Coordinates are measured once, so any scrolling would leave the bubble
  // stranded next to nothing.
  useEffect(() => {
    if (!open) return;

    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);

    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        className={`block shrink-0 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors ${className}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(event) => {
          event.preventDefault();
          open ? hide() : show();
        }}
      >
        <svg className="fill-current" width="16" height="16" viewBox="0 0 16 16">
          <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z" />
        </svg>
      </button>

      {open &&
        createPortal(
          <div
            role="tooltip"
            className="fixed z-50 max-w-xs px-3 py-2 rounded-lg border shadow-lg text-xs bg-white text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700/60"
            style={{
              top: position.top,
              left: position.left,
              transform: position.above
                ? "translate(-50%, -100%)"
                : "translate(-50%, 0)",
            }}
          >
            {children}
          </div>,
          document.body
        )}
    </>
  );
}
